import { useCallback, useMemo } from 'react'

import { QUERY_KEYS } from '~/constants/react-query'
import { REFETCH_TOKEN_VISIBILITY } from '~/constants/refetch-intervals'
import { useActiveOrgId } from '~/hooks/org'
import { useAuthUser } from '~/hooks/useAuthUser'
import { useQueryWrapper } from '~/hooks/useQueryWrapper'
import { useServerRequests } from '~/hooks/useRequests'
import { NFTVisibilityEntry, TokenVisibilityEntry } from '~/types'

type TokenVisibilityResponse = {
  visibilityEntries: {
    tokens: TokenVisibilityEntry[]
    nfts: NFTVisibilityEntry[]
  }
}

export const getTokenVisibilityQueryKey = (activeOrgId: string | undefined) => [
  QUERY_KEYS.TOKEN_VISIBILITY,
  activeOrgId,
]

export const useTokenVisibility = () => {
  const { user: authUser } = useAuthUser()
  const activeOrgId = useActiveOrgId()
  const { serverGetRequest } = useServerRequests()

  const fetchTokenVisibility = useCallback(async () => {
    if (!activeOrgId) {
      throw new Error('No orgId')
    }

    const response = await serverGetRequest<TokenVisibilityResponse>({
      path: `/api/org/token/visibility?orgId=${activeOrgId}`,
    })

    return response!.data
  }, [activeOrgId, serverGetRequest])

  const { data, isLoading, refetch } = useQueryWrapper<TokenVisibilityResponse>(
    {
      queryKey: getTokenVisibilityQueryKey(activeOrgId),
      queryFn: fetchTokenVisibility,
      refetchInterval: REFETCH_TOKEN_VISIBILITY,
      enabled: !!authUser && !!activeOrgId,
      initialData: (): TokenVisibilityResponse | undefined => {
        if (authUser && activeOrgId) {
          return {
            visibilityEntries: { tokens: [], nfts: [] },
          }
        }
      },
    },
  )

  const tokenVisibilityEntries = useMemo(
    () => data?.visibilityEntries?.tokens ?? [],
    [data?.visibilityEntries?.tokens],
  )

  const nftVisibilityEntries = useMemo(
    () => data?.visibilityEntries?.nfts ?? [],
    [data?.visibilityEntries?.nfts],
  )

  return {
    tokenVisibilityEntries,
    nftVisibilityEntries,
    isLoading,
    refetch,
  }
}
